import { FC, Suspense } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Provider as ReduxProvider } from 'react-redux';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { APP_NAMES } from '@savgroup-front-common/constants';
import { ToastContainer } from '@savgroup-front-common/core/atoms/Toast';
import { TooltipProvider } from '@savgroup-front-common/core/atoms/Tooltip/Tooltip';
import { ConfirmationContextProvider } from '@savgroup-front-common/core/components/Confirmation/ConfirmationContextProvider';
import { GraphqlProvider } from '@savgroup-front-common/core/components/GraphqlProvider/GraphqlProvider';
import { GraphQlSseContextProvider } from '@savgroup-front-common/core/components/GraphqlSseContext/GraphqlSseContext';
import { HotjarProvider } from '@savgroup-front-common/core/components/HotjarProvider';
import IntlManager from '@savgroup-front-common/core/components/IntlManager/IntlManager';
import { NewUiProvider } from '@savgroup-front-common/core/components/NewUiProvider/NewUiProvider';
import { BaseLoader } from '@savgroup-front-common/core/molecules/BaseLoader';
import AppErrorBoundary from '@savgroup-front-common/core/molecules/ErrorBoundary/AppErrorBoundary';
import { ToastsProvider } from '@savgroup-front-common/core/molecules/NotificationsProvider';
import { ExternalPortal } from '@savgroup-front-common/core/protons/ExternalPortal/ExternalPortal';
import { KeyboardLayoutProvider } from '@savgroup-front-common/core/protons/KeyboardLayoutProvider';
import { rem } from '@savgroup-front-common/primitive/helpers/units';

import { logCritical } from '../../configuration';
import HOTJAR_CONFIG from '../../configuration/hotjar';

import { buildStore } from './App.config';
import { AppContextProvider } from './App.context';
import DebugLayout from './DebugLayout';
import GlobalStyleManager from './GlobalStyleManager';
import useInitGoogleMap from './hooks/useInitGoogleMap';
import CheckAuthorized from './login/CheckAuthorized';
import { OAuthCallback } from './login/OAuthCallback/OAuthCallback';
import { ThemeProvider } from './ThemeProvider';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const shouldEnableReactQueryDevtools =
  import.meta.env.VITE_ENABLE_REACT_QUERY_DEVTOOLS === 'true';

const App: FC = () => {
  useInitGoogleMap();

  try {
    return (
      <NewUiProvider>
        <GlobalStyleManager />
        <HotjarProvider config={HOTJAR_CONFIG}>
          <ToastsProvider>
            <TooltipProvider>
              <ReduxProvider store={buildStore().store}>
                <KeyboardLayoutProvider>
                  <ThemeProvider>
                    <IntlManager appName={APP_NAMES.FIELD_SERVICE}>
                      <AppErrorBoundary>
                        <GraphQlSseContextProvider>
                          <GraphqlProvider>
                            <GraphQlSseContextProvider>
                              <ConfirmationContextProvider>
                                <Suspense fallback={<BaseLoader />}>
                                  <QueryClientProvider client={queryClient}>
                                    {shouldEnableReactQueryDevtools && (
                                      <ReactQueryDevtools
                                        initialIsOpen={false}
                                      />
                                    )}
                                    <ExternalPortal>
                                      <ToastContainer topOffset={rem(84)} />
                                    </ExternalPortal>

                                    <AppContextProvider>
                                      <BrowserRouter>
                                        <Routes>
                                          <Route
                                            path="/*"
                                            element={<DebugLayout />}
                                          >
                                            <Route
                                              path="callback"
                                              element={<OAuthCallback />}
                                            />

                                            <Route
                                              path="*"
                                              element={<CheckAuthorized />}
                                            />
                                          </Route>
                                        </Routes>
                                      </BrowserRouter>
                                    </AppContextProvider>
                                  </QueryClientProvider>
                                </Suspense>
                              </ConfirmationContextProvider>
                            </GraphQlSseContextProvider>
                          </GraphqlProvider>
                        </GraphQlSseContextProvider>
                      </AppErrorBoundary>
                    </IntlManager>
                  </ThemeProvider>
                </KeyboardLayoutProvider>
              </ReduxProvider>
            </TooltipProvider>
          </ToastsProvider>
        </HotjarProvider>
      </NewUiProvider>
    );
  } catch (err) {
    logCritical(err);
    throw err;
  }
};

export default App;
